/* eslint-disable react-refresh/only-export-components */
import CssBaseline from '@mui/material/CssBaseline';
import { Experimental_CssVarsProvider as CssVarsProvider, useColorScheme } from '@mui/material/styles';
import { createContext, useContext, useMemo, useEffect } from 'react';

import { createTheme } from './create-theme';

import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/material/themeCssVarsAugmentation';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
  mode: 'light' | 'dark';
  toggleMode: () => void;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const ThemeContext = createContext<{
  mode: 'light' | 'dark';
  toggleMode: () => void;
} | null>(null);

// Create a separate component that uses useColorScheme inside CssVarsProvider
function ThemeContent({ children, mode, toggleMode }: Props) {
  const { setColorScheme } = useColorScheme();
  
  // Sync the color scheme when mode changes
useEffect(() => {
  if (setColorScheme) {
    setColorScheme(mode);
  }
}, [mode, setColorScheme]);
;

  const contextValue = useMemo(
    () => ({
      mode,
      toggleMode,
    }),
    [mode, toggleMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <CssBaseline />
      {children}
    </ThemeContext.Provider>
  );
}

export function ThemeProvider({ children, mode, toggleMode }: Props) {
   const theme = useMemo(() => createTheme(mode), [mode]);

  return (
    <CssVarsProvider theme={theme} defaultColorScheme={mode}>
      <ThemeContent mode={mode} toggleMode={toggleMode}>
        {children}
      </ThemeContent>
    </CssVarsProvider>
  );
}